/**
 * VaxInsightCard — clickable Dashboard insight card that surfaces the
 * tenant's current "% pets with up-to-date vaccinations" and links
 * into the Reports → Vaccination Compliance tab.
 *
 * Issue #32 AC3: "Given an insight card on the Dashboard says 'X% vax
 * up-to-date', when I click it, then I drill into the non-compliant
 * pet list."
 *
 * Owns its own fetch so the Dashboard composes it like every other
 * widget. Token comes from readSession() like the other dashboard
 * widgets — keeps the surface area minimal.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchVaccinationComplianceReport,
  readSession,
  type VaccinationComplianceReport,
} from '../../lib/api.js';

interface VaxInsightCardProps {
  apiUrl: string;
  refreshKey?: number;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: VaccinationComplianceReport };

export function VaxInsightCard({
  apiUrl,
  refreshKey = 0,
}: VaxInsightCardProps): JSX.Element {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const load = useCallback(async () => {
    const { token } = readSession();
    if (!token) {
      setState({ kind: 'error', message: 'Not authenticated' });
      return;
    }
    try {
      const data = await fetchVaccinationComplianceReport(apiUrl, token);
      setState({ kind: 'ready', data });
    } catch (err) {
      setState({
        kind: 'error',
        message:
          err instanceof Error ? err.message : 'Unable to load vax compliance',
      });
    }
  }, [apiUrl]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const handleClick = (): void => {
    navigate('/reports?tab=vax');
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="ferro-vax-insight-card"
      data-testid="vax-insight-card"
      aria-label="View vaccination compliance report"
    >
      <span className="ferro-vax-insight-card__label">Vax up-to-date</span>
      {state.kind === 'loading' ? (
        <span className="ferro-vax-insight-card__value">…</span>
      ) : null}
      {state.kind === 'error' ? (
        <span className="ferro-vax-insight-card__value">—</span>
      ) : null}
      {state.kind === 'ready' ? (
        <>
          <span
            className="ferro-vax-insight-card__value"
            data-testid="vax-insight-pct"
          >
            {state.data.compliantPct}%
          </span>
          <span className="ferro-vax-insight-card__meta">
            {state.data.flaggedDogs > 0
              ? `${state.data.flaggedDogs} pet${
                  state.data.flaggedDogs === 1 ? '' : 's'
                } flagged · tap to review`
              : 'All pets compliant'}
          </span>
        </>
      ) : null}
    </button>
  );
}
